'use client';

import { ArrowVariant } from '@/react/components/content/registry/data/parts/sliderSettings';
import {
  Breakpoint,
  Breakpoints
} from '@/react/components/core-ui/Breakpoints';
import { ISliderProps, Slide, Slider } from '@/react/components/Slider';
import type { Nullable } from '@/type-utils';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { SliderDirection } from '@/react/components/Slider/ISliderOptions';
import { useConfig } from '@/react/hooks/useConfig';
import { Swatch, type ISwatchVariation } from './Swatch';
import { SwatchNavigationButton } from './SwatchNavigationButton';
import { FailedHexValidation } from './FailedHexValidation';
import { validateHexValue } from './utils/validateHexValue';

import S from './styles.base.module.scss';

/**
 * A callback that is called to represent a variation of a swatch.
 *
 * First is always the swatch that was target, and the second is the previous swatch if any.
 */
type SwatchVariationCallback = (
  swatches: [ISwatchVariation, Nullable<ISwatchVariation>]
) => void;

interface IProductSwatchSliderProps {
  /**
   * The swatches to display. These represents the product variations.
   */
  swatches: ReadonlyArray<ISwatchVariation>;

  /**
   * Callback to run on hover. This is called when the user hovers over a swatch.
   */
  onHover?: SwatchVariationCallback;

  /**
   * Callback to run on change. This is called when the user selects a new swatch.
   */
  onChange?: SwatchVariationCallback;

  /**
   * Callback to run on leave. This is called when the user hover leaves a swatch.
   */
  onLeave?: SwatchVariationCallback;

  /**
   * The number of swatches to display per slide. If the number of swatches is greater than this number,
   * the swatches will be displayed in a paginated slider. Defaults to 4.
   *
   * @default 4
   */
  swatchesPerSlide: number;
  /**
   * The active swatch if it comes from outside the component.
   * The state can be managed entirely by the component if color is the only attribute
   * being maintained, but once you need more you will probably want to manage state using
   * this prop.
   */
  activeSwatch?: ISwatchVariation;
}

/**
 * A slider that displays swatches for a product.
 */
export const ProductSwatchSlider = observer(function ProductSwatchSlider({
  swatches,
  onHover,
  onChange,
  onLeave,
  swatchesPerSlide = 4,
  activeSwatch
}: IProductSwatchSliderProps) {
  const [selectedSwatch, setSelectedSwatch] = useState<
    Nullable<ISwatchVariation>
  >(activeSwatch ?? null);

  /**
   * This is the currently active hex value based on how the state of this component is
   * being managed. If active swatch is being used at all it will take precedence, if not
   * the component will manage this internally.
   */
  const activeHexValue = activeSwatch
    ? activeSwatch?.hexValue
    : selectedSwatch?.hexValue;

  // Base slider configuration shared between all breakpoints.
  // Width is handled in SCSS via the --swatches-per-slide custom property.
  const baseConfig = {
    height: 'auto',
    slidesPerView: swatchesPerSlide,
    slidesPerGroup: swatchesPerSlide,
    centeredSlides: false,
    direction: SliderDirection.Horizontal,
    sliderClassName: S.slider,
    style: { '--swatches-per-slide': swatchesPerSlide },
    loop: false,
    pagination: { enabled: false }
  } as const;

  const navigation = {
    inset: false,
    arrowVariant: ArrowVariant.AngleLight,
    buttonIconComponent: SwatchNavigationButton,
    buttonClassName: S.navigationButton
  } as const;

  const extraSmallSliderConfig: ISliderProps = {
    ...baseConfig,
    spaceBetween: 4,
    allowTouchMove: true,
    navigation
  };

  const mobileSliderConfig: ISliderProps = {
    ...baseConfig,
    spaceBetween: 4,
    allowTouchMove: true,
    navigation
  };

  const tabletSliderConfig: ISliderProps = {
    ...baseConfig,
    spaceBetween: 6,
    allowTouchMove: true,
    navigation
  };

  const desktopSliderConfig: ISliderProps = {
    ...baseConfig,
    spaceBetween: 4,
    allowTouchMove: swatches.length > swatchesPerSlide,
    navigation
  };

  const config = useConfig('productList');
  const swatchErrorHandling = config.getSetting('swatchErrorHandling').value;
  const hideErroredSwatches = swatchErrorHandling === 'hide';

  const swatchSlides = swatches.map((swatch) => {
    // NOTE: We're doing the validation and rendering a different UI here rather
    // than validating in the `Swatch` component and falling back to the
    // `SwatchErrorBoundary` because the latter was causing the application to crash
    // when interacting with filters. See DECA-5996 for more details.
    try {
      validateHexValue(swatch.hexValue);
    } catch (error) {
      // Slider filters out `null` children so they don't take up space
      if (hideErroredSwatches) return null;
      return (
        <Slide key={swatch.sku}>
          <FailedHexValidation error={error} sku={swatch.sku} />
        </Slide>
      );
    }

    return (
      <Slide key={swatch.sku}>
        <Swatch
          {...swatch}
          skipValidation // we validate above
          active={swatch.hexValue === activeHexValue}
          onHover={onHover && (() => onHover([swatch, selectedSwatch]))}
          onLeave={onLeave && (() => onLeave([swatch, selectedSwatch]))}
          onClick={() => {
            setSelectedSwatch(swatch);
            onChange?.([swatch, selectedSwatch]);
          }}
        />
      </Slide>
    );
  });

  return (
    <div className={S.sliderWrapper}>
      <Breakpoints>
        <Breakpoint media="desktop">
          <Slider {...desktopSliderConfig}>{swatchSlides}</Slider>
        </Breakpoint>
        <Breakpoint media="tablet">
          <Slider {...tabletSliderConfig}>{swatchSlides}</Slider>
        </Breakpoint>
        <Breakpoint media="xs">
          <Slider {...mobileSliderConfig}>{swatchSlides}</Slider>
        </Breakpoint>
        <Breakpoint default>
          <Slider {...extraSmallSliderConfig}>{swatchSlides}</Slider>
        </Breakpoint>
      </Breakpoints>
    </div>
  );
});
